<app-detail-panel [isOpen]="true" [resizable]="true" [(widthPx)]="width">
  @if (document(); as doc) {
    <!-- Header -->
    <div class="panel-header">
      <div class="panel-title-row">
        <app-kind-icon [kind]="getDocKindFromSubtypeCode(doc.type)" />
        <app-editable-panel-title
          [(value)]="title"
          [readonly]="!canManageCompliance() || isGenerating()"
          [ariaLabel]="'panels_ext.document_name_aria' | transloco"
          (commit)="onTitleCommit($event)"
        />
      </div>
      <div class="panel-header-actions">
        <!-- State pill sits top-right, matching the task + control
             drawers. Single state axis (lifecycle status), so it fits
             alongside the action icons without a dedicated sub-hero
             row. -->
        <app-status-indicator
          appearance="inline"
          [status]="getEffectiveStatusVariant(doc)"
          [meaning]="getEffectiveDocumentStatusMeaning(doc)"
          [label]="getEffectiveStatusLabel(doc)"
        />
        <button
          class="panel-icon-btn"
          (click)="onOpenDocument()"
          [title]="'detail_panels.open_document_title' | transloco"
        >
          <mat-icon>open_in_new</mat-icon>
        </button>
        <button
          class="panel-icon-btn"
          (click)="onDownloadDocument()"
          [disabled]="!canExport() || isExporting()"
          [title]="
            isExporting()
              ? ('common.exporting' | transloco)
              : canExport()
                ? ('detail_panels.download_pdf_aria' | transloco)
                : ('panels_ext.no_content_to_export' | transloco)
          "
          [attr.aria-label]="'detail_panels.download_pdf_aria' | transloco"
        >
          @if (isExporting()) {
            <mat-spinner diameter="18"></mat-spinner>
          } @else {
            <mat-icon>download</mat-icon>
          }
        </button>
        <button
          class="panel-icon-btn"
          (click)="onClose()"
          [attr.aria-label]="'detail_panels.close_panel_aria' | transloco"
        >
          <mat-icon>close</mat-icon>
        </button>
      </div>
    </div>

    <!-- No sub-hero. Status pill lives top-right in header-actions
         (single state axis, same slot as the task + control drawers);
         version already renders as a first-class field in the body's
         Details grid. Sub-hero exists only when a drawer has 2+ state
         axes (see assessment-info-panel with lifecycle + review). -->

    <!-- Tabs -->
    <app-panel-tabs
      [tabs]="panelTabs()"
      [activeTab]="activeTab()"
      (tabChange)="setTab($any($event))"
    />

    <!-- Body -->
    <div class="panel-body" role="tabpanel">
      <!-- OVERVIEW -->
      @if (activeTab() === 'overview') {
        <app-document-grounding-notice
          [grounding]="doc.generation_grounding"
          [documentStatus]="doc.status"
        />
        <!-- Keep the AI-source notice above the document content. -->
        @if (
          doc.type === 'DPA' &&
          (!doc.annex_b_source ||
            doc.annex_b_source === 'ai_inferred' ||
            doc.annex_b_source === 'unspecified')
        ) {
          <app-access-banner
            [notice]="{
              tone: 'warning',
              icon: 'warning',
              message: 'panels_ext.annex_b_warning' | transloco,
            }"
          />
        }

        @if (doc.description) {
          <div class="panel-section">
            <div class="panel-section-label">
              {{ 'panels_ext.section_description' | transloco }}
            </div>
            <p class="panel-desc-text">{{ doc.description }}</p>
          </div>
        }

        <div class="panel-section">
          <div class="panel-section-label">{{ 'panels_ext.section_details' | transloco }}</div>
          <div class="panel-details-grid">
            <div class="panel-detail-item">
              <div class="panel-detail-label">{{ 'panels_ext.field_author' | transloco }}</div>
              <div class="panel-detail-value">
                @if (author(); as name) {
                  <div class="panel-avatar-row">
                    <span class="panel-avatar" [style.background]="getAvatarColor(name)">
                      {{ getInitials(name) }}
                    </span>
                    {{ name }}
                  </div>
                } @else {
                  &mdash;
                }
              </div>
            </div>
            <div class="panel-detail-item">
              <div class="panel-detail-label">{{ 'panels_ext.field_type' | transloco }}</div>
              <div class="panel-detail-value">{{ doc | documentSubtypeLabel: locale() }}</div>
            </div>
            <div class="panel-detail-item">
              <div class="panel-detail-label">{{ 'panels_ext.field_created' | transloco }}</div>
              <div class="panel-detail-value">{{ formatDate(doc.created_at, 'compact') }}</div>
            </div>
            <div class="panel-detail-item">
              <div class="panel-detail-label">
                {{ 'panels_ext.field_last_updated' | transloco }}
              </div>
              <div class="panel-detail-value">{{ formatDate(doc.last_updated, 'compact') }}</div>
            </div>
            <div class="panel-detail-item">
              <div class="panel-detail-label">
                {{ 'panels_ext.field_author_organisation' | transloco }}
              </div>
              <div class="panel-detail-value">{{ doc.author_org_name || '—' }}</div>
            </div>
            <div class="panel-detail-item">
              <div class="panel-detail-label">{{ 'panels_ext.field_owner' | transloco }}</div>
              <div class="panel-detail-value">{{ doc.org_name || '—' }}</div>
            </div>
            @if (doc.version) {
              <div class="panel-detail-item">
                <div class="panel-detail-label">
                  {{ 'panels_ext.section_version' | transloco }}
                </div>
                <div class="panel-detail-value">{{ doc.version }}</div>
              </div>
            }
            @if (doc.language) {
              <div class="panel-detail-item">
                <div class="panel-detail-label">
                  {{ 'language_picker.label' | transloco }}
                </div>
                <div class="panel-detail-value">
                  <app-language-badge [language]="doc.language" appearance="inline" />
                </div>
              </div>
            }
          </div>
        </div>

        <app-trust-center-visibility-toggle
          kind="documents"
          [entityId]="doc.document_id"
          [eligible]="isTrustCenterEligible()"
        />

        @if (doc.generation_status === 'COMPLETED' && (doc.context_document_ids?.length ?? 0) > 0) {
          <div class="panel-section">
            <div class="panel-section-label">
              <span>{{ 'panels_ext.section_used_for_generation' | transloco }}</span>
              <span class="context-docs-count" aria-hidden="true">
                {{ doc.context_document_ids?.length ?? 0 }}
              </span>
            </div>
            @if (contextDocsError(); as message) {
              <app-access-banner
                [fullWidth]="true"
                [notice]="{ message, icon: 'error', tone: 'error' }"
              >
                <app-button
                  noticeActions
                  type="secondary"
                  variant="outline"
                  size="small"
                  (clicked)="retryContextDocs()"
                >
                  {{ 'common.retry' | transloco }}
                </app-button>
              </app-access-banner>
            }
            <ul class="context-docs-list" role="list">
              @if (isLoadingContextDocs()) {
                @for (id of doc.context_document_ids ?? []; track id) {
                  <li class="context-doc-item" aria-hidden="true">
                    <div appSkeleton="circle" class="context-doc-skel-icon"></div>
                    <div class="context-doc-info">
                      <div
                        appSkeleton
                        class="context-doc-skel-line context-doc-skel-line--name"
                      ></div>
                      <div
                        appSkeleton
                        class="context-doc-skel-line context-doc-skel-line--meta"
                      ></div>
                    </div>
                  </li>
                }
              } @else {
                @for (ctxDoc of usedContextDocs(); track ctxDoc.context_document_id) {
                  <li class="context-doc-item">
                    <app-type-icon
                      [icon]="getContextDocIconConfig(ctxDoc.file_type).icon"
                      [variant]="getContextDocIconConfig(ctxDoc.file_type).variant"
                      size="small"
                    />
                    <div class="context-doc-info">
                      <span class="context-doc-name" [title]="ctxDoc.file_name">
                        {{ ctxDoc.file_name }}
                      </span>
                      <span class="context-doc-desc">
                        {{ getContextDocFileTypeLabel(ctxDoc.file_type) }}
                        @if (ctxDoc.file_size) {
                          &middot; {{ formatFileSize(ctxDoc.file_size) }}
                        }
                      </span>
                    </div>
                    <button
                      type="button"
                      class="panel-icon-btn"
                      (click)="contextDocPreview.open(ctxDoc)"
                      [attr.aria-label]="
                        'panels_ext.preview_with_name' | transloco: { name: ctxDoc.file_name }
                      "
                      [title]="'detail_panels.preview_title' | transloco"
                    >
                      <mat-icon>visibility</mat-icon>
                    </button>
                  </li>
                }
              }
            </ul>
          </div>
        }
      }

      <!-- CONTROLS -->
      @if (activeTab() === 'controls') {
        <div class="panel-section">
          <div class="panel-rel-header">
            <div class="panel-rel-title">
              <app-section-icon name="controls" />
              {{ 'panels_ext.controls_heading' | transloco }}
              @if (linkedControls().length > 0) {
                <span class="panel-rel-count">{{ linkedControls().length }}</span>
              }
            </div>
            <button
              class="panel-link-btn"
              [class.active]="controlPicker.show()"
              [disabled]="!canManageCompliance()"
              [title]="linkDisabledTooltip()"
              (click)="controlPicker.show() ? controlPicker.close() : controlPicker.open()"
            >
              @if (controlPicker.show()) {
                <mat-icon>close</mat-icon>
                <span>{{ 'panels_ext.cancel' | transloco }}</span>
              } @else {
                <mat-icon>add</mat-icon>
                <span>{{ 'panels_ext.link' | transloco }}</span>
              }
            </button>
          </div>

          <!-- Picker (multi-select with confirm) -->
          @if (controlPicker.show()) {
            <div class="panel-picker">
              <div class="panel-picker-search">
                <mat-icon>search</mat-icon>
                <input
                  type="text"
                  [placeholder]="'detail_panels.search_controls_placeholder' | transloco"
                  [value]="controlPicker.search()"
                  (input)="controlPicker.onSearchInput($event)"
                />
              </div>
              @if (loadingControls()) {
                <div class="panel-picker-empty">
                  {{ 'panels_ext.loading_ellipsis' | transloco }}
                </div>
              } @else if (controlsLoadError(); as message) {
                <!-- Before the empty check: a refused load leaves the list
                     empty too, and "All controls linked" would describe the
                     data rather than the refusal. Shared banner primitive, as
                     on the control panel's two pickers. -->
                <div
                  class="panel-picker-banner"
                  [class.denied-banner]="controlsLoadErrorIsDenial()"
                  [class.error-banner]="!controlsLoadErrorIsDenial()"
                  role="alert"
                >
                  {{ message }}
                </div>
              } @else if (filteredAvailableControls().length === 0) {
                <div class="panel-picker-empty">
                  {{
                    availableControls().length === 0
                      ? ('panels_ext.all_controls_linked' | transloco)
                      : ('panels_ext.search_no_results' | transloco)
                  }}
                </div>
              } @else {
                <div class="panel-picker-list">
                  @for (control of filteredAvailableControls(); track control.control_id) {
                    <button
                      class="panel-picker-item"
                      [class.selected]="controlPicker.isSelected(control.control_id)"
                      (click)="controlPicker.toggleSelection(control.control_id)"
                      [disabled]="controlPicker.linkingBatch()"
                    >
                      <span class="panel-picker-check">
                        <mat-icon>check</mat-icon>
                      </span>
                      <div class="panel-picker-info">
                        <div class="panel-picker-name">{{ control.name }}</div>
                        @if (formatControlSubtitle(control); as sub) {
                          <div class="panel-picker-sub">{{ sub }}</div>
                        }
                      </div>
                    </button>
                  }
                </div>
                @if (controlPicker.selectedIds().size > 0) {
                  <div class="panel-picker-footer">
                    <span class="panel-picker-selection-count">
                      {{
                        'panels_ext.selected_count'
                          | transloco: { count: controlPicker.selectedIds().size }
                      }}
                    </span>
                    <button
                      class="panel-picker-confirm-btn"
                      (click)="confirmLinkControls()"
                      [disabled]="controlPicker.linkingBatch()"
                    >
                      @if (controlPicker.linkingBatch()) {
                        <mat-icon class="panel-picker-spinner">progress_activity</mat-icon>
                        {{ 'panels_ext.linking_ellipsis' | transloco }}
                      } @else {
                        {{ 'panels_ext.link_selected' | transloco }}
                      }
                    </button>
                  </div>
                }
              }
            </div>
          }

          @if (controlPicker.linkError(); as message) {
            <div
              class="panel-picker-banner"
              [class.denied-banner]="controlPicker.linkErrorIsDenial()"
              [class.error-banner]="!controlPicker.linkErrorIsDenial()"
              role="alert"
            >
              {{ message }}
            </div>
          }

          @if (loadingControls()) {
            <div class="panel-muted-text">{{ 'panels_ext.loading_ellipsis' | transloco }}</div>
          } @else if (linkedControls().length > 0) {
            <div class="panel-rel-list">
              @for (control of linkedControls(); track control.control_id) {
                <div
                  class="panel-rel-item panel-rel-item-clickable"
                  role="button"
                  tabindex="0"
                  [attr.aria-label]="'detail_panels.open_control_aria' | transloco"
                  (click)="openLinkedControl(control.control_id)"
                  (keydown.enter)="openLinkedControl(control.control_id)"
                  (keydown.space)="$event.preventDefault(); openLinkedControl(control.control_id)"
                >
                  <div class="panel-rel-icon panel-rel-icon--primary">
                    <app-section-icon name="controls" />
                  </div>
                  <div class="panel-rel-info">
                    <div class="panel-rel-name">{{ control.name }}</div>
                    @if (formatControlSubtitle(control); as sub) {
                      <div class="panel-rel-sub">{{ sub }}</div>
                    }
                  </div>
                  <div class="panel-rel-badges">
                    <app-status-indicator
                      [status]="getControlStatusVariant(control.implementation_status)"
                      [meaning]="getControlStatusMeaning(control.implementation_status)"
                      [label]="
                        getControlStatusLabelShortKey(control.implementation_status) | transloco
                      "
                    />
                  </div>
                  <button
                    class="panel-unlink-btn"
                    (click)="$event.stopPropagation(); unlinkControl(control)"
                    [disabled]="
                      !canManageCompliance() || controlPicker.unlinkingId() === control.control_id
                    "
                    [title]="linkDisabledTooltip()"
                    [attr.aria-label]="'detail_panels.unlink_control_aria' | transloco"
                  >
                    @if (controlPicker.unlinkingId() === control.control_id) {
                      <mat-icon class="panel-picker-spinner">progress_activity</mat-icon>
                    } @else {
                      <mat-icon>link_off</mat-icon>
                    }
                  </button>
                  <mat-icon class="panel-rel-open-icon" aria-hidden="true">chevron_right</mat-icon>
                </div>
              }
            </div>
          } @else if (!controlPicker.show()) {
            <app-empty-state
              variant="panel"
              sectionIcon="controls"
              [title]="'detail_panels.no_controls_linked' | transloco"
              [message]="'panels_ext.no_controls_doc_msg' | transloco"
            />
          }
        </div>
      }

      <!-- TASKS -->
      <app-panel-task-list
        [style.display]="activeTab() !== 'tasks' ? 'none' : null"
        [orgId]="doc.org_id"
        linkedField="document_id"
        [linkedId]="doc.document_id"
        [isManager]="canManageCompliance()"
        [showRecurring]="false"
        [showLinkPicker]="true"
        (taskCount)="onTaskCountChanged($event)"
        (requestClose)="onClose()"
      />

      <!-- HISTORY -->
      @if (activeTab() === 'history') {
        @if (loadingControls()) {
          <div class="panel-section">
            <div class="panel-rel-title">
              <app-section-icon name="controls" />
              {{ 'panels_ext.section_linked_controls' | transloco }}
            </div>
            <div class="panel-muted-text">{{ 'panels_ext.loading_ellipsis' | transloco }}</div>
          </div>
        } @else if (linkedControls().length > 0) {
          <div class="panel-section">
            <div class="panel-rel-title">
              <app-section-icon name="controls" />
              {{ 'panels_ext.section_linked_controls' | transloco }}
              <span class="panel-rel-count">{{ linkedControls().length }}</span>
            </div>
            <div class="panel-rel-list">
              @for (control of linkedControls(); track control.control_id) {
                <div class="panel-rel-item">
                  <div class="panel-rel-icon panel-rel-icon--primary">
                    <app-section-icon name="controls" />
                  </div>
                  <div class="panel-rel-info">
                    <div class="panel-rel-name">{{ control.name }}</div>
                    @if (formatControlSubtitle(control); as sub) {
                      <div class="panel-rel-sub">{{ sub }}</div>
                    }
                  </div>
                  <div class="panel-rel-badges">
                    <app-status-indicator
                      [status]="getControlStatusVariant(control.implementation_status)"
                      [meaning]="getControlStatusMeaning(control.implementation_status)"
                      [label]="
                        getControlStatusLabelShortKey(control.implementation_status) | transloco
                      "
                    />
                  </div>
                </div>
              }
            </div>
          </div>
        }
        <app-audit-timeline
          [events]="auditLoader.auditTrail()"
          [loading]="auditLoader.loadingAuditTrail()"
          [loadFailed]="auditLoader.loadError() !== null"
          [entityNameMap]="linkedEntityNameMap()"
          [entityLabel]="'panels_ext.entity_label_document' | transloco"
          (refresh)="auditLoader.load()"
        />
      }
    </div>
  }
</app-detail-panel>

<!-- Shared preview modal — same component edit-assessment drawer and
     assessment-info-panel use. Triggered from the Used-for-Generation
     list rows; the handler in `shared/context-doc-preview-handler.ts`
     owns open/close/retry/openInNewTab. -->
<app-context-doc-preview [handler]="contextDocPreview" />
